import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, MapPin, ShieldCheck, Phone, Globe, Star, BookOpen, Building2, Video } from 'lucide-react';
import { sections } from '../data/sections';
const stats = [{
  icon: Clock,
  label: 'Operating Schedule',
  value: '24h / 7days'
}, {
  icon: MapPin,
  label: 'Service Radius',
  value: '0–200 mi around Orlando'
}, {
  icon: Globe,
  label: 'Website',
  value: 'View',
  href: 'https://cestruckrepair.com/'
}, {
  icon: Star,
  label: 'Google Business',
  value: 'View',
  href: 'https://maps.app.goo.gl/EUdY9JXpJxHDdfQJ9'
}];
export function Home() {
  const firstSection = sections[0];
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="mx-auto max-w-5xl pb-16" data-id="element-270">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-slate-900 px-6 py-14 sm:px-12 sm:py-20 shadow-2xl mb-10 min-h-[520px] sm:min-h-0 flex items-center" data-id="element-271">
        {/* Cover photo */}
        <img src="https://cdn.magicpatterns.com/uploads/wDtyUmNmafBYSkWUipmHsW/image.png" alt="CES Mobile Truck Repair service fleet at night" className="absolute inset-0 h-full w-full object-cover object-[70%_center] sm:object-center opacity-60" data-id="element-272" />
        {/* Dark gradient overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b sm:bg-gradient-to-r from-slate-950/95 via-slate-900/75 to-slate-900/30" data-id="element-273" />
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" data-id="element-274" />

        <div className="relative z-10 max-w-3xl" data-id="element-275">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-500/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-blue-300 ring-1 ring-inset ring-blue-500/20 mb-6" data-id="element-276">
            <ShieldCheck className="h-3.5 w-3.5" data-id="element-277" />
            Buyer Operations & Transition Portal
          </div>

          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl" data-id="element-278">
            CES Mobile Truck Repair
          </h1>

          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300" data-id="element-279">
            A complete operations and transition manual for the buyer of Cioffi
            Express Services. Use this portal as a reference guide for every
            aspect of the business — from dispatch and pricing to fleet,
            financials, and growth strategy.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-3" data-id="element-280">
            <Link to={`/section/${firstSection.id}`} className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors" data-id="element-281">
              <BookOpen className="h-4 w-4" data-id="element-282" />
              Start with Company Overview
              <ArrowRight className="h-4 w-4" data-id="element-283" />
            </Link>
            <button type="button" onClick={() => document.getElementById('training-library')?.scrollIntoView({
            behavior: 'smooth'
          })} className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-white/10 hover:border-white/30 transition-colors" data-id="element-284">
              <Video className="h-4 w-4" data-id="element-285" />
              Training Library
            </button>
          </div>
        </div>
      </div>

      {/* Operations snapshot */}
      <div className="mb-10" data-id="element-286">
        <div className="mb-5 flex items-end justify-between" data-id="element-287">
          <div data-id="element-288">
            <h2 className="text-xl font-bold tracking-tight text-foreground" data-id="element-289">
              Operations Snapshot
            </h2>
            <p className="text-sm text-muted-foreground mt-1" data-id="element-290">
              Key operational facts at a glance.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-291">
          {stats.map(stat => {
          const Icon = stat.icon;
          const cardClasses = 'rounded-xl border bg-card p-5 shadow-sm transition-all';
          const interactiveClasses = 'hover:border-primary/50 hover:shadow-md group cursor-pointer';
          const inner = <>
                <div className="flex items-center justify-between" data-id="element-292">
                  <div className="flex items-center gap-2 text-muted-foreground" data-id="element-293">
                    <Icon className="h-4 w-4" data-id="element-294" />
                    <span className="text-xs font-medium uppercase tracking-wider" data-id="element-295">
                      {stat.label}
                    </span>
                  </div>
                  {stat.href && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" data-id="element-296" />}
                </div>
                <div className={`mt-3 text-xl font-bold ${stat.href ? 'text-foreground group-hover:text-primary transition-colors' : 'text-foreground'}`} data-id="element-297">
                  {stat.value}
                </div>
              </>;
          if (stat.href) {
            return <a key={stat.label} href={stat.href} target="_blank" rel="noopener noreferrer" className={`${cardClasses} ${interactiveClasses}`} data-id="element-298">
                  {inner}
                </a>;
          }
          return <div key={stat.label} className={cardClasses} data-id="element-299">
                {inner}
              </div>;
        })}
        </div>
      </div>

      {/* Start here pointer */}
      <div className="grid grid-cols-1 gap-6" data-id="element-300">
        <Link to={`/section/${firstSection.id}`} className="group flex flex-col justify-between rounded-xl border bg-card p-6 shadow-sm transition-all hover:border-primary/50 hover:shadow-md" data-id="element-301">
          <div data-id="element-302">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary" data-id="element-303">
              <firstSection.icon className="h-5 w-5" data-id="element-304" />
            </div>
            <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-305">
              Section 01 · Start here
            </div>
            <h3 className="mt-1 text-xl font-bold text-foreground group-hover:text-primary transition-colors" data-id="element-306">
              {firstSection.title}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed" data-id="element-307">
              {firstSection.summary}
            </p>
          </div>
          <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary" data-id="element-308">
            Open section{' '}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" data-id="element-309" />
          </div>
        </Link>
      </div>

      {/* Supplier Network strip */}
      <Link to="/supplier-network" className="group mt-10 block rounded-xl border bg-card p-6 shadow-sm transition-all hover:border-primary/50 hover:shadow-md" data-id="element-310">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" data-id="element-311">
          <div className="flex items-start gap-4" data-id="element-312">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary" data-id="element-313">
              <Building2 className="h-5 w-5" data-id="element-314" />
            </div>
            <div data-id="element-315">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-316">
                Industry Partners
              </div>
              <div className="mt-1 text-lg font-bold text-foreground group-hover:text-primary transition-colors" data-id="element-317">
                Supplier Network
              </div>
              <p className="mt-1 text-sm text-muted-foreground max-w-xl" data-id="element-318">
                Browse the active supplier network — Freightliner, Kenworth,
                Love&apos;s, TA, FleetPride and more — with locations and
                contacts ready for the new owner.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-319">
                {['Freightliner', 'Kenworth', "Love's", 'TA', 'FleetPride', '+ more'].map(name => <span key={name} className="inline-flex items-center rounded-full border bg-background px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground" data-id="element-320">
                    {name}
                  </span>)}
              </div>
            </div>
          </div>
          <div className="inline-flex shrink-0 items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-transform group-hover:translate-x-0.5" data-id="element-321">
            View Network
            <ArrowRight className="h-4 w-4" data-id="element-322" />
          </div>
        </div>
      </Link>

      {/* Contact strip */}
      <div className="mt-6 rounded-xl border bg-muted/40 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-323">
        <div className="flex items-center gap-3" data-id="element-324">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-background border" data-id="element-325">
            <Phone className="h-4 w-4 text-muted-foreground" data-id="element-326" />
          </div>
          <div data-id="element-327">
            <div className="text-sm font-semibold text-foreground" data-id="element-328">
              Need clarification during diligence?
            </div>
            <div className="text-xs text-muted-foreground" data-id="element-329">
              The seller and transition team are available to walk through any
              section in detail.
            </div>
          </div>
        </div>
        <a href="mailto:antoniocioffir@gmail.com?subject=CES%20Mobile%20Truck%20Repair%20%E2%80%94%20Buyer%20Inquiry" className="inline-flex items-center gap-2 rounded-md border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors" data-id="element-330">
          Email antoniocioffir@gmail.com <ArrowRight className="h-4 w-4" data-id="element-331" />
        </a>
      </div>
    </motion.div>;
}